import type { Product, ProductImages, MediaItem, ProductMedia } from '@/types/database'
import type { ProductMediaItem } from '@/types/product-ui'

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function uniqueUrls(urls: string[]): string[] {
  return Array.from(new Set(urls.map(url => url.trim()).filter(Boolean)))
}

/**
 * Get unified media array from product.
 * Uses new media column if available, falls back to legacy fields.
 */
export function getProductMedia(product: Product): MediaItem[] {
  // Try new media array first (cast through unknown for Json type compatibility)
  const mediaArray = product.media as unknown as ProductMedia | undefined
  if (Array.isArray(mediaArray) && mediaArray.length > 0) {
    // Validate each item has required properties
    return mediaArray.filter(item =>
      item && typeof item === 'object' && 'type' in item && 'url' in item
    )
  }

  // Fall back to legacy fields
  const items: MediaItem[] = []

  // Add video if exists
  if (isNonEmptyString(product.video_url)) {
    items.push({
      type: 'video',
      url: product.video_url,
      thumbnail_url: product.video_thumbnail_url || getYouTubeThumbnailFromUrl(product.video_url) || undefined,
    })
  }

  // Add images from product_images array
  const productImages = Array.isArray(product.product_images)
    ? (product.product_images as ProductImages)
    : []

  // Include hero_image_url first if it's not already in product_images
  if (isNonEmptyString(product.hero_image_url)) {
    const heroUrl = product.hero_image_url.trim()
    if (!productImages.some(url => url.trim() === heroUrl)) {
      items.push({ type: 'image', url: heroUrl })
    }
  }

  productImages.forEach(url => {
    if (isNonEmptyString(url)) {
      items.push({ type: 'image', url: url.trim() })
    }
  })

  return items
}

/**
 * Get the primary thumbnail URL for a product.
 * Respects media array order - uses the first item (image or video).
 */
export function getProductThumbnail(product: Product): string | null {
  const media = getProductMedia(product)

  if (media.length === 0) {
    // Ultimate fallback to legacy fields
    return product.hero_image_url || product.video_thumbnail_url || null
  }

  const firstItem = media[0]

  if (firstItem.type === 'image') {
    return firstItem.url
  }

  // First item is video - ALWAYS derive thumbnail from URL for YouTube/Vimeo
  // This prevents stale thumbnails when video URL is changed
  return getVideoThumbnail(firstItem.url, firstItem.thumbnail_url)
}

/**
 * Get video thumbnail - prioritizes deriving from URL over stored thumbnail
 * to prevent stale thumbnails when video URL is updated.
 */
function getVideoThumbnail(videoUrl: string, storedThumbnail?: string): string | null {
  // Try to derive from YouTube URL first (most reliable, always fresh)
  const derivedThumbnail = getYouTubeThumbnailFromUrl(videoUrl)
  if (derivedThumbnail) {
    return derivedThumbnail
  }

  // Fall back to stored thumbnail for non-YouTube videos (Vimeo, etc.)
  return storedThumbnail || null
}

function getYouTubeThumbnailFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')
    let videoId: string | null = null

    if (host === 'youtu.be') {
      videoId = parsed.pathname.split('/').filter(Boolean)[0] ?? null
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v')
      } else if (parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.split('/')[2] ?? null
      } else if (parsed.pathname.startsWith('/shorts/')) {
        videoId = parsed.pathname.split('/')[2] ?? null
      } else if (parsed.pathname.startsWith('/v/')) {
        videoId = parsed.pathname.split('/')[2] ?? null
      }
    }

    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    }
  } catch {
    // Ignore invalid URLs and fall back to any stored/manual thumbnail.
  }

  return null
}

export function getProductVideoThumbnail(product: Product): string | undefined {
  if (isNonEmptyString(product.video_url)) {
    const derivedThumbnail = getYouTubeThumbnailFromUrl(product.video_url)
    if (derivedThumbnail) {
      return derivedThumbnail
    }
  }

  if (isNonEmptyString(product.video_thumbnail_url)) {
    return product.video_thumbnail_url
  }

  return undefined
}

export function getProductImageList(product: Product): string[] {
  const productImages = Array.isArray(product.product_images)
    ? (product.product_images as ProductImages)
    : []

  const images = [
    product.hero_image_url,
    ...productImages,
  ].filter(isNonEmptyString)

  return uniqueUrls(images)
}

export function getPrimaryProductImage(product: Product): string | null {
  const media = getProductMedia(product)

  // Find first image in the unified media array
  const firstImage = media.find(item => item.type === 'image')
  if (firstImage) {
    return firstImage.url
  }

  // Fall back to legacy fields
  const images = getProductImageList(product)
  return images[0] ?? null
}

export function getProductGalleryMedia(product: Product): ProductMediaItem[] {
  const mediaItems = getProductMedia(product)

  return mediaItems.map((item, index): ProductMediaItem => {
    if (item.type === 'video') {
      return {
        kind: 'video',
        url: item.url,
        // ALWAYS derive thumbnail from URL for YouTube to prevent stale thumbnails
        thumbnailUrl: getVideoThumbnail(item.url, item.thumbnail_url) || undefined,
        label: item.is_review ? 'Watch Review' : 'Video',
        isReview: item.is_review,
      }
    }
    return {
      kind: 'image',
      url: item.url,
      label: index === 0 ? 'Primary' : `Image ${index + 1}`,
    }
  })
}

/**
 * Get the designated review video for a product.
 * Returns the video marked as review, or the first video if none marked.
 */
export function getProductReviewVideo(product: Product): MediaItem | null {
  const media = getProductMedia(product)
  const videos = media.filter(item => item.type === 'video')
  if (videos.length === 0) return null
  return videos.find(v => v.is_review) || videos[0]
}
